import apiClient from './apiClient';

// Types for Profile API
export interface UserProfile {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    role: string;
    tenantId?: string;
    facilityId?: string;
    clientId?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface UpdateProfileRequest {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
}

export interface ChangePasswordRequest {
    currentPassword: string;
    newPassword: string;
}

export interface ApiResponse<T> {
    success: boolean;
    message: string;
    data?: T;
}

// Profile API Service
class ProfileService {
    private readonly BASE_PATH = '/profile';

    /**
     * Get current user profile
     */
    async getProfile(): Promise<UserProfile> {
        try {
            const response = await apiClient.get<ApiResponse<UserProfile>>(`${this.BASE_PATH}`);
            if (response.data.success && response.data.data) {
                return response.data.data;
            }
            throw new Error(response.data.message || 'Failed to fetch profile');
        } catch (error: any) {
            console.error('Get profile error:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch profile');
        }
    }

    /**
     * Update user profile
     */
    async updateProfile(profileData: UpdateProfileRequest): Promise<UserProfile> {
        try {
            const response = await apiClient.put<ApiResponse<UserProfile>>(
                `${this.BASE_PATH}`,
                profileData
            );
            if (response.data.success && response.data.data) {
                return response.data.data;
            }
            throw new Error(response.data.message || 'Failed to update profile');
        } catch (error: any) {
            console.error('Update profile error:', error);
            throw new Error(error.response?.data?.message || 'Failed to update profile');
        }
    }

    /**
     * Change user password
     */
    async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
        try {
            const response = await apiClient.post<ApiResponse<null>>(
                `${this.BASE_PATH}/change-password`,
                passwordData
            );
            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to change password');
            }
        } catch (error: any) {
            console.error('Change password error:', error);
            throw new Error(error.response?.data?.message || 'Failed to change password');
        }
    }

    /**
     * Upload profile avatar/photo
     */
    async uploadAvatar(file: File): Promise<string> {
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const response = await apiClient.post<ApiResponse<{ avatarUrl: string }>>(
                `${this.BASE_PATH}/avatar`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success && response.data.data) {
                return response.data.data.avatarUrl;
            }
            throw new Error(response.data.message || 'Failed to upload avatar');
        } catch (error: any) {
            console.error('Upload avatar error:', error);
            throw new Error(error.response?.data?.message || 'Failed to upload avatar');
        }
    }

    /**
     * Delete profile avatar
     */
    async deleteAvatar(): Promise<void> {
        try {
            const response = await apiClient.delete<ApiResponse<null>>(`${this.BASE_PATH}/avatar`);
            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to delete avatar');
            }
        } catch (error: any) {
            console.error('Delete avatar error:', error);
            throw new Error(error.response?.data?.message || 'Failed to delete avatar');
        }
    }
}

// Export singleton instance
const profileService = new ProfileService();
export default profileService;
